import React, { Component } from 'react'
import { navigate } from '@reach/router'
import { StyleSheet, css } from 'aphrodite'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'

import { UserApi } from 'api'
import { User, UserType, pages, operations, UserFilters, KeyValue } from 'types'
import { Table, Header, Page, Select, PagingController, TextInput } from 'components'
import { userEdit, addUser } from 'utils/endpoints'
import { isSuperAdmin } from 'utils/permission'
import AppStore from 'AppStore'
import { getListFilters, persistListFilters } from 'utils/listViewState'
import { matchesListSearch } from 'utils/listFilters'

interface Props {
  path: string
}

interface State {
  users: User[]
  loading: boolean
  filters: UserFilters
  totalCount?: number
  renderedCount: number
}

export default class UsersPage extends Component<Props, State> {
  state: State = {
    loading: true,
    users: [],
    filters: getListFilters(pages.USER_LIST, {
      limit: 10,
      offset: 0,
      premiumStatus: 'any' as const,
      type: 1,
      ...(AppStore.userFilters ?? {}),
    }),
    renderedCount: 0,
  }
  page?: Page

  userType: KeyValue[] = [
    { key: '1', value: 'Admin' },
    { key: '3', value: 'Utenti' },
    { key: '4', value: 'Da confermare' },
  ]

  premiumStatuses: KeyValue[] = [
    { key: 'any', value: 'Qualsiasi stato' },
    { key: 'active', value: 'Premium attivo' },
    { key: 'expired', value: 'Premium scaduto' },
    { key: 'suspended', value: 'Premium sospeso' },
    { key: 'never', value: 'Mai premium' },
  ]

  async componentDidMount() {
    this.refreshUser()
  }

  updateFilter = async (type: 'TYPE' | 'EMAIL' | 'PREMIUM_STATUS', value: any) => {
    const { filters } = this.state
    if (type === 'TYPE') {
      filters.type = value
      filters.premiumStatus = 'any'
    }
    if (type === 'EMAIL') filters.email = value
    if (type === 'PREMIUM_STATUS') filters.premiumStatus = value
    filters.offset = 0
    this.setState({ filters })
    this.refreshUser()
  }

  resetFilters = () => {
    this.setState(
      { filters: { limit: 10, offset: 0, premiumStatus: 'any', type: 1 } },
      this.refreshUser,
    )
  }

  refreshUser = async () => {
    const { filters } = this.state
    this.setState({ loading: true })
    persistListFilters(pages.USER_LIST, filters)
    try {
      let users: User[] = []
      let totalCount = 0
      if (filters.type === 1) {
        const admins = await UserApi.getUsers()
        users = admins.filter((user) => matchesListSearch(filters.email ?? '', [user.email]))
        totalCount = users.length
      } else {
        const response = await Promise.all([
          UserApi.getUsersApp(filters),
          UserApi.getUsersCount(filters),
        ])
        users = response[0]
        totalCount = response[1]
      }
      AppStore.saveUserFilters(this.state.filters)
      this.setState({ users, totalCount, renderedCount: users.length })
    } catch (e) {
      this.page?.showError('Impossibile caricare gli utenti con i filtri selezionati')
    } finally {
      this.setState({ loading: false })
    }
  }

  onEdit = (userId: string) => {
    navigate(userEdit + userId)
  }

  onOperationClick = async (operation: operations, userId: string) => {
    if (operation === 'Modifica') {
      navigate(userEdit + userId)
    }
    if (operation === 'Cancella') {
      this.page?.showConfirmDialog('Cancellazione utente', 'Vuoi davvero cancellare questo utente?', async () => {
        await UserApi.deleteUser(userId)
        this.page?.showNotification({ message: 'Utente cancellato.', type: 'success' })
        this.refreshUser()
      })
    }
    if (operation === 'Conferma') {
      const user = this.state.users.find((user) => user._id === userId)
      try {
        await UserApi.confirmEmail(user?.confirmToken ?? '')
        this.refreshUser()
      } catch (e) {}
    }

    if (operation === 'Accesso') {
      const user = this.state.users.find((user) => user._id === userId)
      await AppStore.clearAsyncStorage()
      await AppStore.saveUser(user)
      navigate('/')
    }
  }

  getValues = (users: User[]): any => {
    const rows = users.map((user: User) => {
      const operations = []
      if (isSuperAdmin()) {
        operations.push('Accesso')
        operations.push('Modifica')
      }

      if (isSuperAdmin() && !user.count) operations.push('Cancella')
      return {
        id: user._id!,
        values: [
          user.name,
          user.surname,
          user.nick,
          user.email,
          user.count || 0,
          user.reviews || 0,
          UserType[user.type],
        ],
        operations: operations,
      }
    })
    return rows
  }

  getAppUser = (users: User[]): any => {
    const rows = users.map((user: User) => {
      const operations = []
      if (isSuperAdmin()) {
        this.state.filters.type === 4 && operations.push('Conferma')
        operations.push('Modifica')
      }
      if (isSuperAdmin() && !user.count) operations.push('Cancella')

      return {
        id: user._id!,
        values: [
          user.name,
          user.surname,
          user.nick,
          user.email,
          user.curriculum ? user.curriculum.university : '---',
          user.quizzes,
          this.getPremiumLabel(user),
        ],
        operations: operations,
      }
    })
    return rows
  }

  getPremiumLabel = (user: User) => {
    const statusLabels = {
      active: 'Attivo',
      expired: 'Scaduto',
      never: 'Mai premium',
      suspended: 'Sospeso',
    }
    const status = user.premiumStatus ? statusLabels[user.premiumStatus] : 'Mai premium'
    if (!user.premiumExpiresAt) return status

    return `${status} · ${new Date(user.premiumExpiresAt).toLocaleDateString('it-IT')}`
  }

  nextPressed = async () => {
    const { renderedCount, totalCount, filters } = this.state
    if (filters.offset! + renderedCount === totalCount) return
    filters.offset = filters.offset! + filters.limit!
    this.refreshUser()
  }

  backPressed = async () => {
    const { filters } = this.state
    if (filters.offset === 0) return
    filters.offset = filters.offset! - filters.limit!
    this.refreshUser()
  }

  render() {
    const keys = ['Nome', 'Cognome', 'Nickname', 'Email', 'Domande create', 'Domande Revisionate', 'Tipo']
    const keysNoAdmin = ['Nome', 'Cognome', 'Nickname', 'Email', 'Università', 'Quiz creati', 'Premium']
    const { filters, loading, users, renderedCount, totalCount } = this.state
    const { offset } = filters
    const buttons = [{ title: 'Inserisci un Utente', href: addUser }]

    return (
      <Page ref={(r) => (this.page = r!)} page={pages.USER_LIST}>
        {isSuperAdmin() && <Header title={'Utenti'} buttons={buttons} />}

        <div style={{ display: 'flex', flexDirection: 'row', flex: 1, alignItems: 'flex-end' }}>
          <Select
            label="Tipo utente:"
            options={this.userType}
            selectedKey={filters.type}
            onChange={(value) => this.updateFilter('TYPE', Number(value))}
            flex={2}
            className={css(s.typeFilter)}
          />
          <TextInput
            label={'Email'}
            value={this.state.filters.email}
            required={false}
            placeholder={'Email'}
            onChange={(value) => this.updateFilter('EMAIL', value)}
            className={css(s.emailFilter)}
            flex={3}
          />
          {filters.type !== 1 && (
            <Select
              label="Stato premium:"
              options={this.premiumStatuses}
              selectedKey={filters.premiumStatus || 'any'}
              onChange={(value) => this.updateFilter('PREMIUM_STATUS', value)}
              flex={2}
              className={css(s.premiumFilter)}
            />
          )}
          <Button variant="outline-secondary" onClick={this.resetFilters} disabled={filters.type === 1 && !filters.email}>
            Azzera filtri
          </Button>
        </div>
        {filters.type !== 1 && (
          <div className={css(s.inlineContainer)}>
            <PagingController
              start={offset! + 1}
              end={offset! + renderedCount}
              count={totalCount || 0}
              nextPressed={this.nextPressed}
              prevPressed={this.backPressed}
              className={css(s.controller)}
            />
          </div>
        )}
        {loading && <Spinner animation="border" />}
        {!loading && filters.type === 1 && (
          <Table keys={keys} rows={this.getValues(users)} onOperationClick={this.onOperationClick} />
        )}

        {!loading && filters.type === 3 && (
          <Table keys={keysNoAdmin} rows={this.getAppUser(users)} onOperationClick={this.onOperationClick} />
        )}
        {!loading && filters.type === 4 && (
          <Table keys={keysNoAdmin} rows={this.getAppUser(users)} onOperationClick={this.onOperationClick} />
        )}
      </Page>
    )
  }
}

const s = StyleSheet.create({
  inlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  filters: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    display: 'flex',
  },
  marginSmall: {
    margin: 4,
  },
  controller: {
    marginTop: 16,
    marginLeft: 10,
  },
  typeFilter: {
    marginRight: 20,
    maxWidth: 250,
  },
  emailFilter: {
    maxWidth: 400,
  },
  premiumFilter: {
    marginLeft: 20,
    maxWidth: 300,
  },
})
